// React Error Boundary for catching and handling component errors

import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { classifyError, logError, type AppError } from '../utils/errorHandling';

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorId: string | null;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: AppError, errorId: string, retry: () => void) => ReactNode;
  onError?: (error: AppError, errorInfo: ErrorInfo) => void;
  isolate?: boolean; // If true, only catches errors from direct children
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const appError = classifyError(error);
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error: appError,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const appError = classifyError(error);
    
    // Log the error
    logError(appError, 'React Error Boundary');
    
    // Call custom error handler if provided
    this.props.onError?.(appError, errorInfo);
    
    // Log additional React-specific information
    console.error('React Error Boundary caught an error:', {
      error: appError,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  retry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorId: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorId) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorId, this.retry);
      }

      // Default error UI
      return (
        <DefaultErrorFallback 
          error={this.state.error}
          errorId={this.state.errorId}
          onRetry={this.retry}
        />
      );
    }

    return this.props.children;
  }
}

// Default error fallback component
interface DefaultErrorFallbackProps {
  error: AppError;
  errorId: string;
  onRetry: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({
  error,
  errorId,
  onRetry,
}) => {
  const handleReportError = () => {
    // In a real app, this would send the error to an error reporting service
    const errorReport = {
      errorId,
      message: error.message,
      code: error.code,
      type: error.type,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    
    console.log('Error report:', errorReport);
    
    // Copy error details to clipboard for easy reporting
    navigator.clipboard?.writeText(JSON.stringify(errorReport, null, 2))
      .then(() => alert('Error details copied to clipboard'))
      .catch(() => console.log('Could not copy to clipboard'));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
              />
            </svg>
          </div>
          
          <h2 className="mt-6 text-3xl font-extrabold text-white">
            {error.type === 'network' && 'Connection Problem'}
            {error.type === 'validation' && 'Validation Error'}
            {error.type === 'timing' && 'Timing Issue'}
            {error.type === 'data' && 'Data Error'}
            {error.type === 'auth' && 'Authentication Error'}
            {error.type === 'session' && 'Session Expired'}
            {error.type === 'permission' && 'Permission Denied'}
            {error.type === 'not_found' && 'Not Found'}
            {error.type === 'server' && 'Server Error'}
            {error.type === 'unknown' && 'Something Went Wrong'}
          </h2>
          
          <p className="mt-2 text-sm text-navy-200">
            {error.userMessage}
          </p>
          
          {error.code && (
            <p className="mt-1 text-xs text-navy-300">
              Error Code: {error.code}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {error.retryable && (
            <button
              onClick={onRetry}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-navy-950 bg-gold-500 hover:bg-gold-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
            >
              Try Again
            </button>
          )}
          
          <button
            onClick={() => window.location.reload()}
            className="group relative w-full flex justify-center py-2 px-4 border border-navy-700 text-sm font-medium rounded-md text-navy-100 bg-navy-800 hover:bg-navy-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
          >
            Refresh Page
          </button>
          
          <button
            onClick={handleReportError}
            className="group relative w-full flex justify-center py-2 px-4 border border-navy-700 text-sm font-medium rounded-md text-navy-100 bg-navy-800 hover:bg-navy-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-500"
          >
            Report Error
          </button>
        </div>

        <div className="text-center">
          <p className="text-xs text-navy-300">
            Error ID: {errorId}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoundary;